import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {FloatOperation, FloatOperationDirection, FloatOperationsPage, WebFloatService} from 'web-angular-client';

import {AlertService} from '../../../../sdk/service/alert.service';
import {rendersPlaceholderOnly} from '../../../../sdk/util/load-error.util';

/**
 * How each operation type ePay documents today is presented: the merchant-facing label and the dot modifier. Both
 * live in one entry so a type cannot be added to the wording and forgotten in the colour. A type outside this map
 * still renders from its raw value rather than being dropped, which covers the window where engine knows a new type
 * and this build does not.
 */
const DOCUMENTED_EVENTS: Readonly<Record<string, {label: string, modifier: string}>> = {
    DEPOSIT: {label: 'Deposit Credited', modifier: 'deposit'},
    REFUND: {label: 'Refund Deducted', modifier: 'refund'},
    REFUND_REVERT: {label: 'Float Restored', modifier: 'revert'},
    FEE: {label: 'Fee Deducted', modifier: 'refund'},
    FEE_REVERT: {label: 'Fee Restored', modifier: 'revert'},
    PAYOUT: {label: 'Payout Sent', modifier: 'refund'},
    PAYOUT_REVERT: {label: 'Payout Restored', modifier: 'revert'},
    MANUAL_ADJUSTMENT: {label: 'Manual Adjustment', modifier: 'adjustment'}
};

/**
 * Shown when operationType is null, which floatSchema.yml defines as ePay having omitted the type; an unlisted type
 * arrives as sent and gets a readable label instead. Direction and description still say what happened.
 */
const UNTYPED_EVENT_LABEL = 'Other';

/** Dot for a type this build cannot place, so an unrecognised event reads as deliberate rather than broken. */
const UNTYPED_DOT_MODIFIER = 'untyped';

/** Operations requested per page. ePay caps this at 100; 25 keeps the first paint small. */
const PAGE_SIZE = 25;

/**
 * The Float Engine audit log on the Float Monitoring page: a forward-only list of the operations behind the
 * merchant's balance, newest first.
 *
 * The paging contract is the one rule to keep in mind when changing this class. The first request sends filters and
 * no token; every later request sends the page token alone. Sending both, or neither, is rejected with a 400. The
 * token also pins the whole session to one snapshot of a history that is otherwise changing underneath us, so it is
 * carried forward untouched rather than recomputed.
 *
 * Paging only goes forward. A failed page therefore leaves the rows already on screen alone, because the merchant
 * cannot page back to recover them.
 */
@Component({
    selector: 'app-float-events',
    templateUrl: './float-events.component.html',
    styleUrls: ['./float-events.component.scss'],
    standalone: false
})
export class FloatEventsComponent implements OnInit, OnDestroy
{
    operations: FloatOperation[] = [];

    nextPageToken: string | null = null;

    loading = false;

    /** Set when a load fails. Drives the placeholder on first load and the retry hint on a later page. */
    loadFailed = false;

    /** True once a request has come back either way, so an empty list is not read as "no activity" while loading. */
    loadAttempted = false;

    /** Set when the caller may not read float activity at all, which no amount of retrying will change. */
    accessDenied = false;

    /** Set when engine rejected the page token itself, so resending it would fail identically every time. */
    pageTokenRejected = false;

    /** ePay error message passed through when a 422 is received. */
    unavailableMessage: string | null = null;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly webFloatService: WebFloatService,
        private readonly alertService: AlertService)
    {
    }

    ngOnInit(): void
    {
        this.load();
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Whether more operations remain. An absent token means the history is exhausted; an empty page does not, which
     * is why this asks the token rather than the list length.
     */
    get hasMore(): boolean
    {
        return this.nextPageToken != null;
    }

    /**
     * Whether the merchant has no float activity at all, as opposed to a list that has not loaded yet or a page that
     * came back empty with more still to fetch.
     */
    get isEmpty(): boolean
    {
        return this.loadAttempted && !this.loadFailed && !this.hasMore && this.operations.length === 0;
    }

    /** Whether the first load failed, leaving nothing to show but a placeholder. */
    get isUnavailable(): boolean
    {
        return this.loadFailed && this.operations.length === 0;
    }

    /**
     * Whether the merchant may not read float activity and has nothing on screen to keep. Gated on an empty list like
     * {@link isUnavailable}, because paging only goes forward: a 403 on a later page must not take away rows the
     * merchant can no longer fetch again.
     */
    get isInaccessible(): boolean
    {
        return this.accessDenied && this.operations.length === 0;
    }

    /** Whether nothing has come back yet, so there are no rows to show and "more" is the wrong word. */
    get isFirstLoad(): boolean
    {
        return this.loading && !this.loadAttempted;
    }

    /**
     * Fetches the next page and appends it. A rejected token has no next page, so this starts a fresh query instead
     * of quietly refetching the first page onto the rows already loaded.
     */
    loadMore(): void
    {
        if (this.pageTokenRejected)
        {
            this.restart();
            return;
        }

        this.load();
    }

    /**
     * Throws away the current session and starts a new query from the beginning. This is the way out of a rejected
     * page token, which cannot be retried.
     *
     * Declines while a request is in flight: load() would refuse the new request and leave the list cleared with
     * nothing on its way to replace it.
     */
    restart(): void
    {
        if (this.loading)
        {
            return;
        }

        this.operations = [];
        this.nextPageToken = null;
        this.pageTokenRejected = false;
        this.loadFailed = false;
        this.loadAttempted = false;
        this.load();
    }

    /**
     * The label for an operation's Event column.
     *
     * @param operation the operation being rendered
     * @returns a documented label, a readable form of a type this build does not map, or "Other" when absent
     */
    eventLabel(operation: FloatOperation): string
    {
        const operationType = operation.operationType;

        if (operationType == null)
        {
            return UNTYPED_EVENT_LABEL;
        }

        const documentedLabel = DOCUMENTED_EVENTS[operationType]?.label;

        if (documentedLabel != null)
        {
            return documentedLabel;
        }

        const readableType = operationType.trim().toLowerCase().replace(/_+/g, ' ');

        return readableType.length === 0
            ? UNTYPED_EVENT_LABEL
            : readableType.charAt(0).toUpperCase() + readableType.slice(1);
    }

    /**
     * The dot colour modifier for an operation's Event column. The template pairs it with the float-events-dot base
     * class.
     *
     * Unlike eventLabel, a type this build does not recognise gets the untyped modifier rather than a value derived
     * from the type: there is no colour to derive, and inventing a class name would match no rule.
     *
     * @param operation the operation being rendered
     * @returns the modifier for a documented type, or the untyped one for anything else
     */
    eventDotModifier(operation: FloatOperation): string
    {
        const operationType = operation.operationType;
        const documentedModifier = operationType == null ? null : DOCUMENTED_EVENTS[operationType]?.modifier;

        return documentedModifier ?? UNTYPED_DOT_MODIFIER;
    }

    /**
     * Whether the operation added to the float, which decides the amount's sign and colour.
     *
     * @param operation the operation being rendered
     * @returns true for a credit, false for a debit
     */
    isCredit(operation: FloatOperation): boolean
    {
        return operation.direction === FloatOperationDirection.CREDIT;
    }

    /**
     * Issues one request: filters on the first call, and only the page token afterwards.
     *
     * A rejected page token is separated from an ordinary failure. A 400 while paging means the token is unusable and
     * resending it can only fail again, so it is discarded and the merchant is offered a fresh start. Every other
     * failure keeps the token, because retrying the same page is exactly the right move after an outage.
     */
    private load(): void
    {
        if (this.loading)
        {
            return;
        }

        const requestedPageToken = this.nextPageToken;

        this.loading = true;
        this.loadFailed = false;
        this.accessDenied = false;
        this.unavailableMessage = null;

        const request = requestedPageToken == null ? {filters: {}} : {pageToken: requestedPageToken};

        this.webFloatService.getFloatHistory(request, PAGE_SIZE)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (page: FloatOperationsPage) =>
                {
                    this.operations = [...this.operations, ...(page.operations ?? [])];
                    this.nextPageToken = page.nextPageToken ?? null;
                    this.loading = false;
                    this.loadAttempted = true;
                },
                error: (err: { status?: number, error?: {errorMessage?: string} }) =>
                {
                    this.loading = false;
                    this.loadAttempted = true;
                    this.loadFailed = true;
                    this.accessDenied = err?.status === 403;

                    if (err?.status === 422)
                    {
                        this.unavailableMessage = err?.error?.errorMessage ?? null;
                    }

                    if (requestedPageToken != null && err?.status === 400)
                    {
                        this.pageTokenRejected = true;
                        this.nextPageToken = null;
                    }

                    if (!rendersPlaceholderOnly(err) && !this.accessDenied && !this.pageTokenRejected
                        && err?.status !== 422)
                    {
                        this.alertService.error(err);
                    }
                }
            });
    }
}
